import { Component, ChangeDetectionStrategy, inject } from "@angular/core";
import { WidgetParent } from "../../class/widget-parent";
import { ComponentAdapterFactory } from "../../class/adapters/component-adapter-factory";
import { SmartPaletteGeneral } from "../../class/smart-palette";
import { ComponentAdapterLabelButton } from "../../class/adapters/component-adapter-label-button";
import { AppSettings } from "../../class/general-classes/app-settings";
import { KeywordTextFormatterAdapter } from "src/model/class/general classes/keyword-text-formatter-adapter";
import { TranslationService } from "src/service/translation.service";
import { PopupService } from "src/service/popup.service";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { Address } from "../../class/component-classes/address";
import { ClickActionType } from "src/model/enum/enumCustomInterface";
import { EnvironmentService } from "src/service/environment.service";
import { Environment } from "../../class/general-classes/environment";
import { IonButton } from "@ionic/angular";
import { SmartComponentSpinnerComponent } from "../smart-component-spinner/smart-component-spinner.component";
import { ErrorLoadingWidgetComponent } from "../error-loading-widget/error-loading-widget.component";

@Component({
    selector: "app-widget-label-button",
    templateUrl: "./widget-label-button.component.html",
    styleUrls: ["./widget-label-button.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [IonButton, SmartComponentSpinnerComponent, ErrorLoadingWidgetComponent]
})

/**
 * Counter entry componet
 */
export class WidgetLabelButtonComponent extends WidgetParent<ComponentAdapterLabelButton> {
  private readonly _translationService = inject(TranslationService);
  private readonly _popupService = inject(PopupService);
  private readonly _environmentService = inject(EnvironmentService);

  private _iconsState: Map<string, string> = new Map();
  private _widgetIsDisplayableSub: Subscription = null;
  private _commandAddress: Address = null;
  private _stateAddress: Address = null;
  private _environment: Environment = null;

  public labelIconSrc: string = "";
  public orientationClass: string = "";
  public keyword: string = "";

  /**
   * When the button is clicked open the commands popup
   */
  public onClickOpenPopupCommands(): void {
    this.openPopupCommands();
  }

  /**
   * Handle popup for sending command
   */
  private async openPopupCommands(): Promise<void> {
    const data =
      await this._popupService.showModalAddressMethodsAndOpenPrompt(
        this._commandAddress,
        true,
        this._commandAddress.hiLevelDataFormat
      );

    if (data.action !== "") {
      this.sendChosenCommand(data.value)
    }
  }

  protected setWidgetSmartPalette(): SmartPaletteGeneral {
    let widgetSmaertPalette = new SmartPaletteGeneral();

    return widgetSmaertPalette;
  }

  protected onComponentLoad(): void {
  }

  protected onSmartComponentErrorLoading(): void { }

  protected onComponentDestroy(): void { }

  protected async onSmartComponentSuccessLoaded(): Promise<void | boolean> {
    const factory = new ComponentAdapterFactory();
    this.componentAdapter = factory.createComponentAdapter(
      this._listSmartComponentSkills,
      ComponentAdapterLabelButton
    );

    this.titleWidget = this.componentAdapter.Name;

    //If component title is empty show error loading component
    if (!this.titleWidget) {
      return this._errorLoading = true;
    }

    //Create a map with key and values for state images
    if (this.componentAdapter.LabelButtonIconsState) {
      this.componentAdapter.LabelButtonIconsState.split('|').forEach((item: string) => {
        //get the value between = and # characters
        if (item) {
          const value = item.match(/(?<=\=)(.*?)(?=\#)/)[0].toString();
          const img = item.split("#")[1];
          this._iconsState.set(value, img);
        }
      });
    }

    //Set the src for img with the default value
    if (this.componentAdapter.LabelButtonDefaultImage) {
      this.labelIconSrc = AppSettings.URL_ICONS_REPOSITORY + encodeURIComponent(this.componentAdapter.LabelButtonDefaultImage);
    }

    this.classImgPosition = this.getImgPosition(this.componentAdapter.LabelButtonOrientation);

    this._widgetIsDisplayableSub = this._widgetIsDisplayable.pipe(
      filter((isReady) => isReady),
    ).subscribe(async (isReady) => {
      if (this.componentAdapter.LabelButtonAddressState) {
        this._stateAddress = this._addresses.filter(item => item.id === this.componentAdapter.LabelButtonAddressState.id)[0];

        if (!this._stateAddress) {
          this._errorLoading = true;
          return;
        }

        //Replace the keyword with the selected value of the address for keywords that are dependant from state address
        if (!(this.componentAdapter.LabelButtonFormatting.includes('#roomname') || this.componentAdapter.LabelButtonFormatting.includes('#roomnumber'))) {
          this.getKeywordValueForStateAddress();
        }
      }

      if (this.componentAdapter.LabelButtonAddressCommand) {
        const matchingCommand = this._addresses.filter(item => item.id === this.componentAdapter.LabelButtonAddressCommand.id)[0];
        if (!matchingCommand) {
          this._errorLoading = true;
          return;
        }
        this._commandAddress = new Address(matchingCommand);
      }

      if (this.componentAdapter.LabelButtonFormatting.includes('#roomname') || this.componentAdapter.LabelButtonFormatting.includes('#roomnumber')) {
        await this.getKeywordRoomNameNumber();
      }
    });
  }

  /**
   * Replace the keyword with the correct value
   */
  private getKeywordValueForStateAddress(): void {
    if (this.componentAdapter.LabelButtonFormatting.includes('#decimaldigits')) {
      this.formatDecimalDigits();
    } else {
      this.keyword = new KeywordTextFormatterAdapter(this.componentAdapter.LabelButtonFormatting, 1,
        true,
        this._translationService).replaceText(this._stateAddress, null, null, null);
    }
  }

  /**
   * Replace #roomname and #roomnumber keyword with the value got from environment API 
   */
  private async getKeywordRoomNameNumber(): Promise<void> {
    if (!this._environment) {
      const env = await this._environmentService.getEnvironments(null, this._idEnvironment).toPromise();
      this._environment = env.data[0];

      if (this.componentAdapter.LabelButtonFormatting.includes('#roomnumber')) {
        this.keyword = this.componentAdapter.LabelButtonFormatting.replace("#roomnumber", this._environment.translatedNRoom);
      } else {
        this.keyword = this.componentAdapter.LabelButtonFormatting.replace("#roomname", this._environment.translatedCustomDescription);
      }
    }
  }

  /**
   * Format value if keyword is #decimaldigitsN
   */
  private formatDecimalDigits(): void {
    const string = this.componentAdapter.LabelButtonFormatting;
    const isReversed = !string.startsWith('#');

    //If isReversed is false split by empty space, else by keywords
    const splitString = !isReversed ? string
      .split(/(?<=^\S+)\s/)
      .filter((item: string) => item) :
      string
        .split(AppSettings.PATTERN_LABEL_BUTTON_KEYWORD)
        .filter((item: string) => item);

    const decimalDigits = !isReversed ? parseInt(splitString.filter((item: string) => item.startsWith("#"))[0].split('#decimaldigits')[1])
      : parseInt(splitString[splitString.indexOf('#decimaldigits') + 1]);

    let currentValue = this._stateAddress.lastValue.toString().replace(",", ".");
    currentValue = this._translationService.formatDecimal(parseFloat(currentValue), true, decimalDigits);

    this.keyword = string.replace(`#decimaldigits${decimalDigits}`, currentValue);
  }

  /**
   * Handle sending command according to the chosen option
   * @param value value to send
   */
  private sendChosenCommand(value: string): void {
    switch (value) {
      case ClickActionType.READ:
        if (this._stateAddress != null) {

          this._mqttService.publishAddressRead(
            this._stateAddress.instanceGUID +
            "_" +
            this._stateAddress.address
          );
        }
        break;
      case ClickActionType.NO_ACTIONS:
        // do nothing
        break;
      default:
        if (value) {
          this.sendAddressCommand(this._commandAddress.instanceGUID, this._commandAddress.address, value);
        }
    }
  }

  /**
   * Get icon according to current state value
   * @param instanceGUID 
   * @param address 
   * @param value 
   */
  private getImgCurrentState(instanceGUID: string,
    address: string,
    value: string): void {
    if (
      instanceGUID === this.componentAdapter.LabelButtonAddressState.instanceGUID &&
      address === this.componentAdapter.LabelButtonAddressState.address
    ) {
      if (this._iconsState.has(value) && this._iconsState.get(value) !== "") {
        this.labelIconSrc = AppSettings.URL_ICONS_REPOSITORY + encodeURIComponent(this._iconsState.get(value));
      } else if ((!this._iconsState.has(value) || this._iconsState.get(value) === "") && this.componentAdapter.LabelButtonDefaultImage) {
        this.labelIconSrc = AppSettings.URL_ICONS_REPOSITORY + encodeURIComponent(this.componentAdapter.LabelButtonDefaultImage);
      }

      //Update keyword too, otherwise value is not updated
      if (this._stateAddress) {
        this.getKeywordValueForStateAddress();
      }
    }
  }

  protected onAddressValueChange(
    instanceGUID: string,
    address: string,
    value: string
  ): void {

    //Change the icon based on current state
    if (this.componentAdapter.LabelButtonAddressState) {
      this.getImgCurrentState(instanceGUID, address, value);
    }
  }
}
